import {isPhysicalPrintJob, isPrefetchPrintJob} from '../mappers/fromPrintJobs'
import {formatTimestamp, getLatestMillis, toMillis} from '../time'
import {PrintStoryDoc} from '../types'
import {
  buildContentPrintSummary,
  ConfirmedBySource,
  formatConfirmedBySource,
  LastRequestSnapshot,
  PrefetchDeviceStats,
} from './contentPrintSummary'

export type PrintFlowOutcomeKey =
  | 'ready_no_print'
  | 'ready_with_delivery_warning'
  | 'preparing_printable'
  | 'caching'
  | 'ready_on_printer'
  | 'print_requested'
  | 'printing'
  | 'printed'
  | 'partially_printed'
  | 'print_failed'
  | 'blocked_offline'
  | 'unknown'

export type PrintFlowTone = 'success' | 'info' | 'neutral' | 'warning' | 'danger'

export type StageStatus =
  | 'complete'
  | 'sent_unconfirmed'
  | 'sent_timed_out'
  | 'not_requested'
  | 'pending'
  | 'failed'
  | 'partial'
  | 'na'
  | 'unknown'

export type PrintFlowOutcome = {
  key: PrintFlowOutcomeKey
  title: string
  subtitle: string
  detail: string
  tone: PrintFlowTone
  attention: string[]
  stats: {
    printableCodes: number
    printersTargeted: number
    cacheConfirmations: number
    cacheExpected: number
    printJobs: number
    printAttempts: number
    copiesRequested: number
    copiesConfirmed: number
    autoRequestedCopies: number
    confirmedCopiesTotal: number
    prefetchDeliveryAttempts: number
    confirmedBySource: ConfirmedBySource
  }
  lastRequest: LastRequestSnapshot
  prefetch: PrefetchDeviceStats
  confirmedBySource: ConfirmedBySource
  expected: {
    printableRenders: number
    prefetchDeliveries: number
    physicalPrintJobs: number
    copies: number
  }
  observed: {
    printableRendersCompleted: number
    prefetchPublished: number
    prefetchConfirmations: number
    physicalPrintJobsPublished: number
    copiesConfirmed: number
  }
  derived: {
    rendering: 'matched' | 'incomplete' | 'failed'
    prefetchPublishing: 'matched' | 'incomplete' | 'na'
    prefetchConfirmation: 'matched' | 'incomplete' | 'na'
    physicalPrinting: 'matched' | 'incomplete' | 'failed' | 'na'
  }
  stages: Array<{
    key: string
    label: string
    status: StageStatus
    statusLabel: string
    explanation: string
  }>
  codes: {
    consumer: Array<{
      codeId: string
      code?: string
      printableReady: boolean
      prefetchDeviceCount: number
      assignedPrinter?: string
    }>
    staff: Array<{
      codeId: string
      code?: string
      printableReady: boolean
      assignedPrinter?: string
      prefetchDeviceCount: number
    }>
  }
  devices: Array<{
    deviceId: string
    role: 'assigned' | 'cache_target' | 'actual_printer'
    connectionLabel: string
    heartbeatLabel: string
    heartbeatAt?: number
    assetsSent: number
    cacheConfirmed: number
    latestCommandAt?: number
    physicalPrint: string
    issue?: string
    jobCount: number
  }>
  semanticEvents: Array<{
    ts: number
    title: string
    detail?: string
  }>
  printRequested: boolean
  lastRealEventAt?: number
}

const isStaffCode = (code: PrintStoryDoc) => code.isStaffCode === true

const hasPrintable = (code: PrintStoryDoc) =>
  Boolean(code.printableReady || code.printableUrl || code.contentUrl || code.url)

const isPrefetchConfirmed = (job: PrintStoryDoc) => {
  const status = String(job.status || '').toLowerCase()
  if (['completed', 'complete', 'success', 'succeeded'].includes(status)) {
    return true
  }
  const result = String(job.mqttCallback?.result || '').toLowerCase()
  const stage = String(job.mqttCallback?.stage || '').toLowerCase()
  return result === 'success' && stage === 'prefetch'
}

const isJobFailed = (job: PrintStoryDoc) => {
  const status = String(job.status || '').toLowerCase()
  return ['failed', 'error', 'errored', 'blocked', 'callback_missing'].includes(status)
}

const isJobPublished = (job: PrintStoryDoc) => {
  const status = String(job.status || '').toLowerCase()
  const delivery = String(job.deliveryStatus || '').toLowerCase()
  return (
    ['published', 'pending', 'completed', 'failed', 'callback_missing'].includes(status) ||
    delivery.includes('mqtt_published') ||
    Boolean(job.publishedAtMs || job.timestamps?.publishedAt)
  )
}

const getPrinterHealthLabel = (
  status?: PrintStoryDoc,
  connection?: PrintStoryDoc
): {connectionLabel: string; heartbeatLabel: string; heartbeatAt?: number; issue?: string} => {
  const heartbeatAt =
    toMillis(status?.lastHeartbeatAtMs) ||
    toMillis(status?.lastHeartbeatAt) ||
    toMillis(status?.lastPingTimestamp) ||
    toMillis(status?.lastPing) ||
    toMillis(status?.updatedAt)

  const ageMs = heartbeatAt ? Date.now() - heartbeatAt : undefined
  const normalized = String(status?.normalizedStatus || status?.status || '').toLowerCase()
  const severity = String(status?.severity || '').toLowerCase()
  const connected = connection?.connected === true
  const lastConnAt = toMillis(connection?.lastEventAtMs) || toMillis(connection?.updatedAt)

  if (status?.missing) {
    return {
      connectionLabel: 'No status doc',
      heartbeatLabel: 'Not available',
      issue: 'No recent heartbeat is available',
    }
  }

  if (severity === 'connectivity' || (ageMs !== undefined && ageMs > 90_000)) {
    return {
      connectionLabel: connected ? 'Connected (stale heartbeat)' : 'Offline',
      heartbeatLabel: heartbeatAt ? formatTimestamp(heartbeatAt) : 'Missing',
      heartbeatAt,
      issue: 'Device heartbeat is offline / missing',
    }
  }

  if (!connected && lastConnAt && Date.now() - lastConnAt > 24 * 60 * 60 * 1000) {
    return {
      connectionLabel: 'Stale / unknown',
      heartbeatLabel: heartbeatAt ? formatTimestamp(heartbeatAt) : 'Not available',
      heartbeatAt,
      issue: 'Connection evidence is stale',
    }
  }

  if (connected) {
    return {
      connectionLabel: 'Live',
      heartbeatLabel: heartbeatAt ? formatTimestamp(heartbeatAt) : normalized || 'OK',
      heartbeatAt,
    }
  }

  return {
    connectionLabel: status ? 'Reachable' : 'Unknown',
    heartbeatLabel: heartbeatAt ? formatTimestamp(heartbeatAt) : 'Not available',
    heartbeatAt,
    issue: status ? undefined : 'No evidence available',
  }
}

export const buildPrintFlowOutcome = ({
  content,
  codes = [],
  renderJobs = [],
  printJobs = [],
  printerStatuses = [],
  printerConnections = [],
  callbackLogs = [],
  emails = [],
}: {
  content?: PrintStoryDoc | null
  codes?: PrintStoryDoc[]
  renderJobs?: PrintStoryDoc[]
  printJobs?: PrintStoryDoc[]
  printerStatuses?: PrintStoryDoc[]
  printerConnections?: PrintStoryDoc[]
  callbackLogs?: PrintStoryDoc[]
  emails?: string[]
}): PrintFlowOutcome => {
  const prefetchJobs = printJobs.filter(isPrefetchPrintJob)
  const physicalJobs = printJobs.filter(isPhysicalPrintJob)

  const printRequested = Boolean(
    physicalJobs.length > 0 ||
      content?.autoPrint === true ||
      content?.autoPrintRequestId ||
      content?.autoPrintRequestedAt ||
      Number(content?.lastPrintRequestedCopies || content?.autoPrintRequestedCopies || 0) > 0
  )

  const consumerCodes = codes.filter((code) => !isStaffCode(code))
  const staffCodes = codes.filter(isStaffCode)

  const printableReadyCount = codes.filter(hasPrintable).length
  const renderCompletedCount = Math.max(
    printableReadyCount,
    renderJobs.filter((job) =>
      ['completed', 'complete', 'success', 'succeeded'].includes(String(job.status || '').toLowerCase())
    ).length
  )
  const renderFailed = renderJobs.some(isJobFailed)

  const printSummary = buildContentPrintSummary({content, printJobs})
  const {
    autoRequestedCopies,
    confirmedCopiesTotal,
    physicalPrintJobs,
    printAttempts,
    confirmedBySource,
    lastRequest,
    prefetch,
  } = printSummary
  const autoConfirmedCopies = confirmedBySource.auto
  const sourceBreakdownText = formatConfirmedBySource(confirmedBySource)

  const prefetchPublished = prefetchJobs.filter(isJobPublished).length
  const prefetchFailed = prefetchJobs.some(isJobFailed)

  const deviceIds = Array.from(
    new Set(prefetchJobs.map((job) => String(job.deviceId || '')).filter(Boolean))
  )

  const assignedPrinter =
    String(
      staffCodes.find((c) => c.deviceId)?.deviceId ||
        codes.find((c) => c.deviceId)?.deviceId ||
        physicalJobs.find((j) => j.deviceId)?.deviceId ||
        ''
    ) || undefined

  const printFailed = physicalJobs.some(isJobFailed)
  const printCompleted = physicalPrintJobs > 0
  const printPublished = physicalJobs.some(isJobPublished)
  const autoIntentUnfilled =
    autoRequestedCopies > 0 && autoConfirmedCopies < autoRequestedCopies

  const expectedPrintable = Math.max(codes.length, renderJobs.length, printableReadyCount)
  const expectedPrefetch = prefetch.uniqueTargetDevices
  const expectedPrintJobs = printRequested ? Math.max(physicalPrintJobs, 1) : 0
  const expectedCopies = printRequested ? autoRequestedCopies : 0

  const derived = {
    rendering: renderFailed
      ? ('failed' as const)
      : renderCompletedCount >= Math.max(1, expectedPrintable) || (codes.length > 0 && printableReadyCount === codes.length)
      ? ('matched' as const)
      : ('incomplete' as const),
    prefetchPublishing:
      prefetchJobs.length === 0
        ? ('na' as const)
        : prefetchPublished >= prefetchJobs.length
        ? ('matched' as const)
        : ('incomplete' as const),
    prefetchConfirmation:
      prefetch.uniqueTargetDevices === 0
        ? ('na' as const)
        : prefetch.confirmedDevices >= prefetch.uniqueTargetDevices
        ? ('matched' as const)
        : ('incomplete' as const),
    physicalPrinting: !printRequested
      ? ('na' as const)
      : printFailed && physicalPrintJobs === 0
      ? ('failed' as const)
      : printCompleted && !autoIntentUnfilled
      ? ('matched' as const)
      : ('incomplete' as const),
  }

  const assignedStatus = assignedPrinter
    ? printerStatuses.find((s) => s.deviceId === assignedPrinter || s.id === assignedPrinter)
    : undefined
  const assignedOffline =
    String(assignedStatus?.severity || '').toLowerCase() === 'connectivity' ||
    assignedStatus?.heartbeatFreshness === 'missing'

  let key: PrintFlowOutcomeKey = 'unknown'
  let title = 'Unknown — insufficient evidence'
  let subtitle = 'Not enough evidence to determine print readiness.'
  let detail = ''
  let tone: PrintFlowTone = 'neutral'

  if (renderFailed) {
    key = 'preparing_printable'
    title = 'Preparing printable — render failed'
    subtitle = 'At least one printable render failed.'
    tone = 'danger'
  } else if (codes.length > 0 && printableReadyCount < codes.length && renderJobs.some((j) => String(j.status).toLowerCase() === 'pending')) {
    key = 'preparing_printable'
    title = 'Preparing printable'
    subtitle = 'Printable assets are still being generated.'
    tone = 'info'
  } else if (!printRequested) {
    if (prefetchJobs.length > 0 && prefetch.confirmedDevices < prefetch.uniqueTargetDevices) {
      key = 'ready_with_delivery_warning'
      title = 'Ready for printing — delivery warning'
      subtitle = 'No physical print was requested.'
      detail = `Content and printable assets were created successfully. Prefetch commands were sent to ${prefetch.uniqueTargetDevices} printer(s), but cache confirmations are incomplete (${prefetch.confirmedDevices} of ${prefetch.uniqueTargetDevices} devices).`
      tone = 'info'
    } else if (prefetchJobs.length > 0 && prefetch.confirmedDevices >= prefetch.uniqueTargetDevices) {
      key = 'ready_on_printer'
      title = 'Ready on printer — no print requested'
      subtitle = 'Assets appear cached. No physical print was requested.'
      tone = 'success'
    } else {
      key = 'ready_no_print'
      title = 'Ready for printing — no print requested'
      subtitle = 'The content and printable assets were created successfully.'
      detail = 'No physical print was requested for this content.'
      tone = 'success'
    }
  } else if (printFailed && assignedOffline) {
    key = 'blocked_offline'
    title = 'Blocked — printer offline'
    subtitle = 'Print could not complete while the assigned printer is offline.'
    tone = 'danger'
  } else if (printFailed) {
    key = 'print_failed'
    title = 'Print failed'
    subtitle = 'A physical print job failed.'
    tone = 'danger'
  } else if (printCompleted && autoIntentUnfilled) {
    key = 'partially_printed'
    title = 'Partially printed'
    subtitle = `${autoConfirmedCopies} of ${autoRequestedCopies} AUTO copies confirmed.`
    detail =
      confirmedCopiesTotal > autoConfirmedCopies
        ? `${confirmedCopiesTotal} copies confirmed in total${
            sourceBreakdownText ? ` (${sourceBreakdownText})` : ''
          }.`
        : ''
    tone = 'warning'
  } else if (printCompleted) {
    key = 'printed'
    title = 'Printed successfully'
    subtitle =
      confirmedCopiesTotal > 0 ? `${confirmedCopiesTotal} copies confirmed.` : 'Print completed.'
    detail = [
      autoRequestedCopies > 0 ? `AUTO ${autoRequestedCopies} requested` : '',
      sourceBreakdownText ? `By source: ${sourceBreakdownText}` : '',
    ]
      .filter(Boolean)
      .join('. ')
    tone = 'success'
  } else if (printPublished) {
    key = 'printing'
    title = 'Printing'
    subtitle = 'Print command published; awaiting station callback.'
    tone = 'info'
  } else {
    key = 'print_requested'
    title = 'Print requested'
    subtitle = 'A physical print has been requested.'
    tone = 'info'
  }

  if (assignedOffline && !printRequested && key === 'ready_with_delivery_warning') {
    // keep info tone — offline is attention, not "print failed"
  }

  const attention: string[] = []
  for (const deviceId of deviceIds) {
    const status = printerStatuses.find((s) => s.deviceId === deviceId || s.id === deviceId)
    const connection = printerConnections.find((c) => c.deviceId === deviceId || c.id === deviceId)
    const health = getPrinterHealthLabel(status, connection)
    if (health.issue) {
      attention.push(`${deviceId}: ${health.issue}${health.heartbeatAt ? ` (last heartbeat ${formatTimestamp(health.heartbeatAt)})` : ''}`)
    }
  }
  if (!printRequested && prefetchJobs.length > 0 && prefetch.confirmedDevices === 0) {
    attention.push('No printer has confirmed that printable assets were cached.')
  }

  const prefetchStatus: StageStatus =
    prefetch.uniqueTargetDevices === 0 && prefetchJobs.length === 0
      ? 'na'
      : prefetchFailed
      ? 'failed'
      : prefetch.uniqueTargetDevices > 0 && prefetch.confirmedDevices >= prefetch.uniqueTargetDevices
      ? 'complete'
      : prefetchPublished > 0
      ? 'sent_timed_out'
      : 'pending'

  const stages: PrintFlowOutcome['stages'] = [
    {
      key: 'content',
      label: 'Content',
      status: content ? 'complete' : 'unknown',
      statusLabel: content ? 'Complete' : 'Unknown',
      explanation: content ? 'Content created and uploaded.' : 'Content record missing.',
    },
    {
      key: 'codes',
      label: 'Codes',
      status: codes.length > 0 ? 'complete' : 'pending',
      statusLabel: codes.length > 0 ? 'Complete' : 'Pending',
      explanation:
        codes.length > 0
          ? `${consumerCodes.length} consumer · ${staffCodes.length} staff`
          : 'No codes found yet.',
    },
    {
      key: 'render',
      label: 'Printable',
      status: renderFailed ? 'failed' : printableReadyCount >= codes.length && codes.length > 0 ? 'complete' : renderJobs.length ? 'pending' : 'unknown',
      statusLabel: renderFailed
        ? 'Failed'
        : printableReadyCount >= codes.length && codes.length > 0
        ? 'Ready'
        : 'Preparing',
      explanation:
        codes.length > 0
          ? `${printableReadyCount} of ${codes.length} printable assets ready.`
          : 'No printable assets yet.',
    },
    {
      key: 'prefetch',
      label: 'Prefetch',
      status: prefetchStatus,
      statusLabel:
        prefetchStatus === 'complete'
          ? 'Confirmed cached'
          : prefetchStatus === 'sent_timed_out'
          ? 'Sent — confirmation not received'
          : prefetchStatus === 'failed'
          ? 'Failed'
          : prefetchStatus === 'na'
          ? 'Not started'
          : 'Pending',
      explanation:
        prefetchJobs.length === 0
          ? 'No prefetch commands found.'
          : `${prefetch.confirmedDevices} of ${prefetch.uniqueTargetDevices} device(s) confirmed · ${prefetch.deliveryAttempts} delivery attempt(s).`,
    },
    {
      key: 'print',
      label: 'Print',
      status: !printRequested
        ? 'not_requested'
        : printFailed && physicalPrintJobs === 0
        ? 'failed'
        : printCompleted
        ? 'complete'
        : printPublished
        ? 'pending'
        : 'pending',
      statusLabel: !printRequested
        ? 'Not requested'
        : printFailed && physicalPrintJobs === 0
        ? 'Failed'
        : printCompleted
        ? 'Completed'
        : 'Print requested',
      explanation: !printRequested
        ? 'No physical print command exists.'
        : `${physicalPrintJobs} successful print job(s) · ${printAttempts} attempt(s) · ${confirmedCopiesTotal} copies confirmed.`,
    },
    {
      key: 'result',
      label: 'Result',
      status: !printRequested
        ? 'na'
        : printCompleted
        ? autoIntentUnfilled
          ? 'partial'
          : 'complete'
        : printFailed && physicalPrintJobs === 0
        ? 'failed'
        : 'pending',
      statusLabel: !printRequested
        ? 'Not applicable'
        : printCompleted
        ? autoIntentUnfilled
          ? 'Partial'
          : 'Printed'
        : printFailed && physicalPrintJobs === 0
        ? 'Failed'
        : 'In progress',
      explanation: !printRequested
        ? 'Zero copies expected.'
        : [
            `${confirmedCopiesTotal} confirmed ${
              confirmedCopiesTotal === 1 ? 'copy' : 'copies'
            } across ${physicalPrintJobs} successful print ${
              physicalPrintJobs === 1 ? 'job' : 'jobs'
            }.`,
            autoRequestedCopies > 0 ? `AUTO ${autoRequestedCopies} requested.` : '',
            sourceBreakdownText,
          ]
            .filter(Boolean)
            .join(' '),
    },
  ]

  const mapCode = (code: PrintStoryDoc) => {
    const codeId = String(code.codeId || code.id)
    const related = prefetchJobs.filter((job) => String(job.codeId || '') === codeId)
    const devices = Array.from(new Set(related.map((j) => String(j.deviceId || '')).filter(Boolean)))
    return {
      codeId,
      code: code.code ? String(code.code) : undefined,
      printableReady: hasPrintable(code),
      prefetchDeviceCount: devices.length,
      assignedPrinter: code.deviceId ? String(code.deviceId) : undefined,
    }
  }

  const devices = deviceIds.map((deviceId) => {
    const jobs = prefetchJobs.filter((job) => String(job.deviceId || '') === deviceId)
    const status = printerStatuses.find((s) => s.deviceId === deviceId || s.id === deviceId)
    const connection = printerConnections.find((c) => c.deviceId === deviceId || c.id === deviceId)
    const health = getPrinterHealthLabel(status, connection)
    const latestCommandAt = getLatestMillis(
      jobs.flatMap((job) => [job.publishedAtMs, job.timestamps?.publishedAt, job.updatedAt])
    )
    const role: 'assigned' | 'cache_target' | 'actual_printer' =
      assignedPrinter === deviceId
        ? 'assigned'
        : physicalJobs.some((j) => String(j.deviceId) === deviceId)
        ? 'actual_printer'
        : 'cache_target'

    return {
      deviceId,
      role,
      connectionLabel: health.connectionLabel,
      heartbeatLabel: health.heartbeatLabel,
      heartbeatAt: health.heartbeatAt,
      assetsSent: jobs.filter(isJobPublished).length,
      cacheConfirmed: jobs.filter(isPrefetchConfirmed).length,
      latestCommandAt,
      physicalPrint: printRequested
        ? physicalJobs.some((j) => String(j.deviceId) === deviceId)
          ? 'Targeted'
          : 'Not targeted'
        : 'Not requested',
      issue:
        jobs.length > 0 && jobs.every((j) => !isPrefetchConfirmed(j))
          ? 'Device has not confirmed receipt'
          : health.issue,
      jobCount: jobs.length,
    }
  })

  // Also include assigned printer even with no prefetch
  if (assignedPrinter && !deviceIds.includes(assignedPrinter)) {
    const status = printerStatuses.find((s) => s.deviceId === assignedPrinter || s.id === assignedPrinter)
    const connection = printerConnections.find(
      (c) => c.deviceId === assignedPrinter || c.id === assignedPrinter
    )
    const health = getPrinterHealthLabel(status, connection)
    devices.unshift({
      deviceId: assignedPrinter,
      role: 'assigned',
      connectionLabel: health.connectionLabel,
      heartbeatLabel: health.heartbeatLabel,
      heartbeatAt: health.heartbeatAt,
      assetsSent: 0,
      cacheConfirmed: 0,
      latestCommandAt: undefined,
      physicalPrint: printRequested ? 'Assigned' : 'Not requested',
      issue: health.issue,
      jobCount: 0,
    })
  }

  const semanticEvents: PrintFlowOutcome['semanticEvents'] = []
  const contentCreated =
    toMillis(content?.dateCreated) || toMillis(content?.createdAt) || toMillis(content?.createdOn)
  if (contentCreated) {
    semanticEvents.push({ts: contentCreated, title: 'Content created'})
  }
  if (emails.length > 0 && contentCreated) {
    semanticEvents.push({
      ts: contentCreated,
      title: `Emails recorded for ${emails.length} recipient${emails.length === 1 ? '' : 's'}`,
    })
  }
  for (const code of staffCodes) {
    const ts = toMillis(code.createdAt) || toMillis(code.dateCreated) || toMillis(code.createdAtMs)
    if (ts) {
      semanticEvents.push({
        ts,
        title: `Staff code ${code.code || code.codeId || code.id} assigned`,
        detail: code.deviceId ? `Assigned printer: ${code.deviceId}` : undefined,
      })
    }
  }
  const renderStart = getLatestMillis(
    renderJobs.map((j) => j.timestamps?.createdAt || j.createdAt || j.createdAtMs)
  )
  if (renderStart) {
    semanticEvents.push({ts: renderStart, title: 'Printable rendering started'})
  }
  const consumerTs = getLatestMillis(
    consumerCodes.map((c) => c.createdAt || c.dateCreated || c.createdAtMs)
  )
  if (consumerTs && consumerCodes.length) {
    semanticEvents.push({
      ts: consumerTs,
      title: `Consumer code${consumerCodes.length > 1 ? 's' : ''} ${consumerCodes
        .map((c) => c.code || c.codeId || c.id)
        .join(', ')} assigned`,
    })
  }
  const renderDone = getLatestMillis(
    renderJobs
      .filter((j) => ['completed', 'complete', 'success'].includes(String(j.status || '').toLowerCase()))
      .map((j) => j.timestamps?.completedAt || j.completedAt || j.updatedAt)
  )
  if (renderDone || (printableReadyCount > 0 && contentCreated)) {
    semanticEvents.push({
      ts: renderDone || consumerTs || contentCreated || Date.now(),
      title: `${Math.max(printableReadyCount, renderCompletedCount)} printable asset(s) completed`,
    })
  }
  if (deviceIds.length > 0) {
    const prefetchStart = getLatestMillis(
      prefetchJobs.map((j) => j.timestamps?.createdAt || j.createdAt || j.publishedAtMs)
    )
    if (prefetchStart) {
      semanticEvents.push({
        ts: prefetchStart,
        title: `Prefetch started for ${deviceIds.join(' and ')}`,
      })
    }
    const prefetchPub = getLatestMillis(prefetchJobs.map((j) => j.publishedAtMs || j.timestamps?.publishedAt))
    if (prefetchPub) {
      semanticEvents.push({
        ts: prefetchPub,
        title: `${prefetchPublished} device delivery command(s) published`,
      })
    }
  }
  if (prefetchJobs.length > 0 && prefetch.confirmedDevices === 0) {
    const lastPrefetch = getLatestMillis(
      prefetchJobs.map((j) => j.publishedAtMs || j.timestamps?.publishedAt || j.updatedAt)
    )
    semanticEvents.push({
      ts: (lastPrefetch || 0) + 1,
      title: 'No device confirmations received',
    })
  }
  if (!printRequested) {
    const anchor =
      getLatestMillis(prefetchJobs.map((j) => j.publishedAtMs || j.updatedAt)) ||
      renderDone ||
      contentCreated
    if (anchor) {
      semanticEvents.push({
        ts: anchor + 2,
        title: 'No physical print requested',
      })
    }
  } else {
    const printTs = getLatestMillis(
      physicalJobs.map((j) => j.publishedAtMs || j.timestamps?.createdAt || j.createdAt)
    )
    if (printTs) {
      semanticEvents.push({
        ts: printTs,
        title: `Physical printing ${
          printCompleted ? 'completed' : printFailed && physicalPrintJobs === 0 ? 'failed' : 'requested'
        }`,
        detail: printCompleted
          ? `${confirmedCopiesTotal} ${
              confirmedCopiesTotal === 1 ? 'copy' : 'copies'
            } confirmed across ${physicalPrintJobs} print ${
              physicalPrintJobs === 1 ? 'job' : 'jobs'
            }`
          : undefined,
      })
    }
  }

  semanticEvents.sort((a, b) => a.ts - b.ts)

  const lastRealEventAt = getLatestMillis([
    ...semanticEvents.map((e) => e.ts),
    ...prefetchJobs.map((j) => j.publishedAtMs || j.updatedAt),
    ...physicalJobs.map((j) => j.publishedAtMs || j.updatedAt),
    ...renderJobs.map((j) => j.updatedAt || j.completedAt),
    ...codes.map((c) => c.createdAt || c.updatedAt),
    content?.dateCreated,
    content?.updatedOn,
  ])

  // silence unused
  void callbackLogs

  return {
    key,
    title,
    subtitle,
    detail,
    tone,
    attention,
    stats: {
      printableCodes: codes.length,
      printersTargeted: devices.length,
      cacheConfirmations: prefetch.confirmedDevices,
      cacheExpected: prefetch.uniqueTargetDevices,
      printJobs: physicalPrintJobs,
      printAttempts,
      copiesRequested: autoRequestedCopies,
      copiesConfirmed: confirmedCopiesTotal,
      autoRequestedCopies,
      confirmedCopiesTotal,
      prefetchDeliveryAttempts: prefetch.deliveryAttempts,
      confirmedBySource,
    },
    lastRequest,
    prefetch,
    confirmedBySource,
    expected: {
      printableRenders: expectedPrintable,
      prefetchDeliveries: expectedPrefetch,
      physicalPrintJobs: expectedPrintJobs,
      copies: expectedCopies,
    },
    observed: {
      printableRendersCompleted: renderCompletedCount,
      prefetchPublished,
      prefetchConfirmations: prefetch.confirmedDevices,
      physicalPrintJobsPublished: physicalPrintJobs,
      copiesConfirmed: confirmedCopiesTotal,
    },
    derived,
    stages,
    codes: {
      consumer: consumerCodes.map(mapCode),
      staff: staffCodes.map(mapCode),
    },
    devices,
    semanticEvents,
    printRequested,
    lastRealEventAt,
  }
}
